import cn from 'clsx'

import React from 'react'
import InlineSVG from 'svg-inline-react'
import { getTextColor } from '@disco/utils/colors'
import { getEntityName } from '@disco/utils/entityName'
import { getInitials } from '@disco/utils/string-utils'
import styles from './index.module.css'

type Props = {
  name?: string | Record<string, any>
  size?: string
  fontSize?: string
  className?: string
  nodeRight?: React.ReactNode
}

/**
 * A user avatar component that displays user initials or a default icon with customizable styling
 * @param name - The user's name (string or object), used to generate initials and background color
 * @param size - The size of the avatar (defaults to '32px')
 * @param fontSize - The font size for the initials (defaults to '14px')
 * @param className - Optional CSS class name
 * @param nodeRight - Optional content to display on the right side of the avatar
 * @returns A user avatar component with initials or default user icon
 */
const UserAvatar = ({
  name = '',
  size = '32px',
  fontSize = '14px',
  className,
  nodeRight,
}: Props): React.ReactElement<'div'> => {
  const formattedName = typeof name === 'string' ? name : getEntityName(name)
  const backgroundColor = formattedName
    ? getTextColor(formattedName)
    : '#c1c7d0'
  const style = {
    backgroundColor,
    width: size,
    height: size,
    lineHeight: size,
    fontSize,
  }
  const content = (
    <div className={cn(styles.userAvatar, className)} style={style}>
      {formattedName ? (
        getInitials(formattedName)
      ) : (
        <InlineSVG
          src={require(`!svg-inline!assets/icons/user.svg`)}
          width="24"
          height="24"
          raw
        />
      )}
    </div>
  )

  if (nodeRight) {
    return (
      <div className={styles.userAvatarContainer}>
        <div className={styles.userAvatarRightNode}>{nodeRight}</div>
        {content}
      </div>
    )
  }

  return content
}

export default UserAvatar
